import { useState, useEffect, useRef } from "react";
import { useSubscription } from "../../../../subscription";
import { useTranslation } from "react-i18next";
import { FiMic, FiCamera, FiEdit, FiUpload, FiVideo, FiInfo } from "react-icons/fi";
import { MonitorPlay } from "lucide-react";
import { SectionLabel, Surface } from "../../../../../shared/ui/primitives";
import Button from "../../../../../shared/ui/button/Button";
import { useBreakpoint } from "../../../../../hooks/useBreakpoint";
import { VideoCallRecorder } from "../../../../../services/video-call-recorder";
import { useAlchelyMeetingAdopted } from "../../../../marketplace";
import { useAiInsightsGate } from "../../../../ai_insights";

type SessionType =
  | "live"
  | "image_notes"
  | "manual_notes"
  | "upload_audio"
  | "video_call"
  | "alchely_meeting";

interface Props {
  onSelect: (type: SessionType) => void;
  onBack?: () => void;
  /** Offered for a session that has not happened yet: keep it pending, to prepare it. */
  onSaveForLater?: () => void;
}

export default function SessionAddChooseForm({ onSelect, onBack, onSaveForLater }: Props) {
  const { t } = useTranslation();
  const { isPremium } = useSubscription();
  const { isMobile, isTablet } = useBreakpoint();
  const [selected, setSelected] = useState<SessionType | null>(null);
  const continueButtonRef = useRef<HTMLDivElement>(null);
  const optionRefs = useRef<Partial<Record<SessionType, HTMLButtonElement | null>>>({});

  useEffect(() => {
    if (selected && (isMobile || isTablet) && continueButtonRef.current) {
      continueButtonRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [selected, isMobile, isTablet]);

  const videoCallSupported = VideoCallRecorder.isSupported();
  const alchelyMeetingAdopted = useAlchelyMeetingAdopted();
  const { adopted: memoryAdopted } = useAiInsightsGate();

  /**
   * The rows the options are laid out in, by kind of work rather than by
   * popularity: hold the session here, hand us a recording, or write it up
   * yourself. A row whose options are all filtered out simply does not render,
   * so the shape survives a therapist without the tools for the first row.
   */
  const ROWS: SessionType[][] = [
    ["alchely_meeting", "video_call", "live"],
    ["upload_audio", "image_notes"],
    ["manual_notes"],
  ];

  const options: {
    type: SessionType;
    title: string;
    subtitle: string;
    icon: React.ReactNode;
    premiumOnly: boolean;
    /**
     * Whether the option only makes sense with Memoria aumentada. Every way of
     * handing Alchely raw material — a recording, a captured call, a photo of
     * your notes — exists so that something reads it. Without the tool nothing
     * would, so the option is absent rather than offered and disappointing.
     */
    needsMemory?: boolean;
    unavailableReason?: string;
  }[] = [
    // Absent rather than disabled, like every other unadopted tool: the
    // therapist has not taken it from the catalogue, which is not a fault to
    // explain in the middle of creating a session.
    ...(alchelyMeetingAdopted
      ? [
          {
            type: "alchely_meeting" as const,
            title: t("session.alchelyMeetingSession"),
            subtitle: t("session.alchelyMeetingSessionDesc"),
            icon: <FiVideo size={28} />,
            premiumOnly: true,
          },
        ]
      : []),
    {
      type: "live" as const,
      title: t("session.liveSession"),
      subtitle: t("session.liveSessionDesc"),
      icon: <FiMic size={28} />,
      premiumOnly: true,
      needsMemory: true,
    },
    {
      type: "video_call" as const,
      title: t("session.videoCallSession"),
      subtitle: t("session.videoCallSessionDesc"),
      // The same screen-with-play the quick "New session" modal uses for it.
      icon: <MonitorPlay size={28} aria-hidden="true" />,
      premiumOnly: true,
      needsMemory: true,
      // Shown but not selectable where the browser cannot share tab audio:
      // hiding it would read as a missing feature rather than a browser limit.
      unavailableReason: videoCallSupported
        ? undefined
        : t("session.videoCall.unsupported"),
    },
    {
      type: "upload_audio" as const,
      title: t("session.audioSession"),
      subtitle: t("session.audioSessionDesc"),
      icon: <FiUpload size={28} />,
      premiumOnly: true,
      needsMemory: true,
    },
    {
      type: "image_notes" as const,
      title: t("session.imageSession"),
      subtitle: t("session.imageSessionDesc"),
      icon: <FiCamera size={28} />,
      premiumOnly: false,
      needsMemory: true,
    },
    // Writing the session up yourself needs no tool at all, and is what remains
    // when none are adopted.
    {
      type: "manual_notes" as const,
      title: t("session.manualSession"),
      subtitle: t("session.manualSessionDesc"),
      icon: <FiEdit size={28} />,
      premiumOnly: false,
    },
  ];

  type Option = (typeof options)[number];

  /** Whether this therapist sees the option at all. */
  const visible = (o: Option) =>
    !(o.premiumOnly && !isPremium) && !(o.needsMemory && !memoryAdopted);

  /** Every option the therapist can actually pick, in the order they appear. */
  const selectable = ROWS.flatMap((row) =>
    row
      .map((type) => options.find((option) => option.type === type))
      .filter((option): option is Option =>
        Boolean(option) && visible(option!) && !option!.unavailableReason
      )
  );

  /** Arrow keys move between options and wrap, per the ARIA radiogroup pattern. */
  const handleKeyDown = (event: React.KeyboardEvent, type: SessionType) => {
    const offset =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (offset === 0) return;

    event.preventDefault();
    const index = selectable.findIndex((option) => option.type === type);
    const next = selectable[(index + offset + selectable.length) % selectable.length];
    if (!next) return;
    setSelected(next.type);
    optionRefs.current[next.type]?.focus();
  };

  const renderOption = (o: Option) => {
    const unavailable = Boolean(o.unavailableReason);
    const isSelected = selected === o.type;
    // The first selectable option holds the tab stop until something is chosen.
    const inTabOrder = isSelected || (selected === null && selectable[0]?.type === o.type);

    return (
      <button
        key={o.type}
        type="button"
        role="radio"
        aria-checked={isSelected}
        aria-describedby={o.unavailableReason ? `${o.type}-reason` : undefined}
        disabled={unavailable}
        tabIndex={unavailable ? -1 : inTabOrder ? 0 : -1}
        ref={(node) => {
          optionRefs.current[o.type] = node;
        }}
        onKeyDown={(event) => !unavailable && handleKeyDown(event, o.type)}
        onClick={() => !unavailable && setSelected(o.type)}
        className={`relative flex flex-col items-center rounded-xl border p-6 text-center
          transition-colors duration-[--duration-fast] ease-[--ease-standard]
          ${unavailable
            ? "cursor-not-allowed border-border-subtle opacity-60"
            : isSelected
              ? "border-accent bg-accent-tint"
              : "border-border-subtle bg-surface-raised hover:border-border-strong hover:bg-surface-sunken/50"
          }
        `}
      >
        <span
          aria-hidden="true"
          className={`mb-4 flex justify-center ${
            unavailable
              ? "text-content-tertiary"
              : isSelected
                ? "text-accent-on-tint"
                : "text-accent-text"
          }`}
        >
          {o.icon}
        </span>
        <span className="mb-1 font-semibold text-content-primary">{o.title}</span>
        <span className="text-sm text-content-secondary">{o.subtitle}</span>
        {o.unavailableReason && (
          <span
            id={`${o.type}-reason`}
            className="mt-3 flex items-start gap-1.5 text-left text-sm text-status-warning"
          >
            <FiInfo className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>{o.unavailableReason}</span>
          </span>
        )}
      </button>
    );
  };

  return (
    <Surface as="section" padding="lg" aria-labelledby="choose-mode-heading">
      <SectionLabel as="h2" id="choose-mode-heading" className="mb-6">
        {t("session.howToAddNotes")}
      </SectionLabel>

      {/* One grid per row, all on the same three-column track, so a row that
          does not fill it leaves a gap rather than resizing its cards — the
          options stay the same size wherever they sit. */}
      <div role="radiogroup" aria-labelledby="choose-mode-heading" className="flex flex-col gap-6">
        {ROWS.map((row, index) => {
          const cards = row
            .map((type) => options.find((option) => option.type === type))
            .filter((option): option is Option => Boolean(option) && visible(option!));

          if (cards.length === 0) return null;

          return (
            <div
              key={index}
              className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {cards.map(renderOption)}
            </div>
          );
        })}
      </div>

      <div className="flex justify-center gap-3 pt-8" ref={continueButtonRef}>
        <Button variant="outline" onClick={onBack}>
          {t("session.back")}
        </Button>
        {onSaveForLater && (
          <Button variant="outline" onClick={onSaveForLater}>
            {t("session.upcoming.saveForLater")}
          </Button>
        )}
        <Button
          disabled={!selected}
          onClick={() => selected && onSelect(selected)}
        >
          {t("session.continue")}
        </Button>
      </div>
    </Surface>
  );
}
